import { CommonModule } from '@angular/common';
import { Component, computed, effect, OnDestroy, Signal } from '@angular/core';
import { AttachmentQueueEntry, AttachmentQueueService } from '@app/_services/attachment-queue.service';
import { ConnectivityService } from '@app/_services/connectivity.service';
import { SettingsService } from '@app/_services/settings.service';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { log } from '@app/_services/logger';

/**
 * UploadProgressComponent - Displays upload queue progress.
 *
 * This component shows:
 * - List of pending/uploading attachments with progress bars
 * - Retry buttons for failed uploads
 * - "Waiting for network" indicator when offline
 * - Cancel button for pending uploads
 *
 * Pattern matches Flutter's upload queue UI showing real-time progress.
 */
@Component({
  selector: 'app-upload-progress',
  standalone: true,
  imports: [
    CommonModule,
    SvgComponent
  ],
  templateUrl: './upload-progress.component.html',
  styleUrl: './upload-progress.component.scss'
})
export class UploadProgressComponent implements OnDestroy {
  /** Whether currently online */
  isOnline: Signal<boolean>;

  /** Queue entries to display */
  queueEntries: Signal<AttachmentQueueEntry[]>;

  /** Number of pending uploads */
  pendingCount: Signal<number>;

  /** Whether any uploads are in progress */
  isProcessing: Signal<boolean>;

  /** Whether the component should be visible (has items to show) */
  isVisible: Signal<boolean>;

  /** Get entries grouped by status for display */
  activeEntries: Signal<AttachmentQueueEntry[]>;

  /** Get failed entries that can be retried */
  failedEntries: Signal<AttachmentQueueEntry[]>;

  constructor(
    private queueService: AttachmentQueueService,
    private connectivityService: ConnectivityService,
    public ss: SettingsService
  ) {
    this.isOnline = this.connectivityService.isOnline;
    this.queueEntries = this.queueService.queueEntries;
    this.pendingCount = this.queueService.pendingCount;
    this.isProcessing = this.queueService.isProcessing;

    // Compute active entries (not complete)
    this.activeEntries = computed(() => {
      return this.queueEntries().filter(e =>
        e.status !== 'complete'
      );
    });

    // Compute failed entries
    this.failedEntries = computed(() => {
      return this.queueEntries().filter(e =>
        e.status === 'failed' && e.retryCount < e.maxRetries
      );
    });

    // Determine visibility based on having active entries
    this.isVisible = computed(() => {
      return this.activeEntries().length > 0;
    });

    // Log when visibility changes for debugging
    effect(() => {
      const visible = this.isVisible();
      const count = this.activeEntries().length;
      if (visible) {
        log.debug('UploadProgressComponent', 'Showing upload progress', { count });
      }
    });
  }

  /**
   * Get the display status text for an entry.
   */
  getStatusText(entry: AttachmentQueueEntry): string {
    if (!this.isOnline()) {
      return this.ss.t('dataUploadingProcess_offlineInfo');
    }

    switch (entry.status) {
      case 'pending':
        return this.ss.t('dataUploadingProcess_uploadingInfo');
      case 'uploading':
        return `${entry.uploadProgress}%`;
      case 'processing':
        return 'Processing...';
      case 'failed':
        return entry.lastError || 'Upload failed';
      case 'complete':
        return 'Complete';
      default:
        return '';
    }
  }

  /**
   * Get the icon name for the file type.
   */
  getFileTypeIcon(entry: AttachmentQueueEntry): string {
    switch (entry.fileType) {
      case 'image':
        return 'image';
      case 'video':
        return 'play';
      case 'audio':
        return 'audio';
      case 'document':
        return 'document';
      default:
        log.warn('UploadProgressComponent.getFileTypeIcon', `Unknown fileType: ${entry.fileType}`, { fileType: entry.fileType });
        return 'document';
    }
  }

  /**
   * Retry a failed upload.
   */
  async retryUpload(entry: AttachmentQueueEntry): Promise<void> {
    log.debug('UploadProgressComponent', 'Retrying upload', { id: entry.id });
    await this.queueService.updateEntry(entry.id, { status: 'pending' });
  }

  /**
   * Cancel a pending upload.
   */
  async cancelUpload(entry: AttachmentQueueEntry): Promise<void> {
    log.debug('UploadProgressComponent', 'Cancelling upload', { id: entry.id });
    await this.queueService.removeFromQueue(entry.id);
  }

  /**
   * Retry all failed uploads.
   */
  async retryAll(): Promise<void> {
    log.debug('UploadProgressComponent', 'Retrying all failed uploads');
    const failed = this.failedEntries();
    for (const entry of failed) {
      await this.queueService.updateEntry(entry.id, { status: 'pending' });
    }
  }

  /**
   * Truncate filename for display.
   */
  truncateFilename(filename: string, maxLength: number = 20): string {
    if (filename.length <= maxLength) {
      return filename;
    }

    const ext = filename.includes('.') ? filename.split('.').pop() : '';
    const nameWithoutExt = filename.includes('.')
      ? filename.substring(0, filename.lastIndexOf('.'))
      : filename;

    const truncatedName = nameWithoutExt.substring(0, maxLength - 3 - (ext?.length || 0));
    return ext ? `${truncatedName}...${ext}` : `${truncatedName}...`;
  }

  /**
   * Format file size for display.
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const size = bytes / Math.pow(k, i);

    return `${size.toFixed(i > 0 ? 1 : 0)} ${sizes[i]}`;
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }
}
